import React from 'react';
import { MessageCircle, X, MapPin, Phone } from 'lucide-react';
import { normalizeWhatsAppNumber, buildWaMeLink } from '../../../lib/whatsapp';
import VenueBookingRequestForm from './VenueBookingRequestForm';
import { AmenitiesGrid } from './AmenitiesGrid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VenueDetailModal({ venue, onClose }) {
  const whatsappLink = () => {
    const number = normalizeWhatsAppNumber(venue.whatsapp_number);
    if (!number) return null;
    return buildWaMeLink(number, `مرحباً، أرغب بالاستفسار عن ملعب ${venue.name}`);
  };

  const wa = whatsappLink();
  const callNumber = normalizeWhatsAppNumber(venue.whatsapp_number);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="venue-detail-modal" dir="rtl">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-[#16233B] p-6 text-white no-scrollbar">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <h3 className="text-xl font-bold" data-testid="venue-detail-name">{venue.name}</h3>
            <p className="text-sm text-slate-400">{venue.club_name} {venue.city ? `— ${venue.city}` : ''}</p>
          </div>
          <button onClick={onClose} data-testid="venue-detail-close-btn" className="shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {venue.has_image && (
          <img src={`${API}/venues/${venue.id}/image`} alt={venue.name} className="mb-4 h-52 w-full rounded-xl object-cover" />
        )}

        <div className="mb-4 grid grid-cols-3 gap-2" data-testid="venue-detail-action-buttons">
          {venue.maps_url ? (
            <a href={venue.maps_url} target="_blank" rel="noreferrer" data-testid="venue-directions-btn" className="flex flex-col items-center gap-1 rounded-xl border border-white/10 py-2.5 text-xs font-bold text-slate-200 transition-colors hover:border-teal-400/50 hover:text-teal-300">
              <MapPin className="h-4 w-4" /> الاتجاهات
            </a>
          ) : (
            <span className="flex flex-col items-center gap-1 rounded-xl border border-white/5 py-2.5 text-xs font-bold text-slate-600" data-testid="venue-directions-unavailable">
              <MapPin className="h-4 w-4" /> الاتجاهات
            </span>
          )}
          {callNumber ? (
            <a href={`tel:+${callNumber}`} data-testid="venue-call-btn" className="flex flex-col items-center gap-1 rounded-xl border border-white/10 py-2.5 text-xs font-bold text-slate-200 transition-colors hover:border-teal-400/50 hover:text-teal-300">
              <Phone className="h-4 w-4" /> اتصال
            </a>
          ) : (
            <span className="flex flex-col items-center gap-1 rounded-xl border border-white/5 py-2.5 text-xs font-bold text-slate-600" data-testid="venue-call-unavailable">
              <Phone className="h-4 w-4" /> اتصال
            </span>
          )}
          {wa ? (
            <a href={wa} target="_blank" rel="noreferrer" data-testid="venue-whatsapp-btn" className="flex flex-col items-center gap-1 rounded-xl border border-[#25D366]/30 py-2.5 text-xs font-bold text-[#25D366] transition-colors hover:bg-[#25D366]/10">
              <MessageCircle className="h-4 w-4" /> واتساب
            </a>
          ) : (
            <span className="flex flex-col items-center gap-1 rounded-xl border border-white/5 py-2.5 text-xs font-bold text-slate-600" data-testid="venue-whatsapp-unavailable">
              <MessageCircle className="h-4 w-4" /> واتساب
            </span>
          )}
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {venue.sport_type && <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">{venue.sport_type}</span>}
          {venue.dimensions && <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">مقاس {venue.dimensions}</span>}
          {venue.capacity && <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">سعة {venue.capacity}</span>}
          {venue.women_friendly === true && <span className="rounded-full bg-pink-500/20 px-3 py-1 text-xs font-semibold text-pink-300">مناسب للسيدات</span>}
          {venue.price_per_hour && <span className="rounded-full bg-teal-500/20 px-3 py-1 text-xs font-bold text-teal-300">{venue.price_per_hour} ر.س / ساعة</span>}
        </div>

        {venue.amenities?.length > 0 && <div className="mb-4"><AmenitiesGrid amenityIds={venue.amenities} /></div>}

        {venue.description && <p className="mb-4 text-sm leading-relaxed text-slate-300">{venue.description}</p>}

        <VenueBookingRequestForm venue={venue} />
      </div>
    </div>
  );
}
